import { BookingDetails, TimeSlotPicker, WidgetTimePicker } from '../../../redux/types';
import { DayState, WidgetType } from '../../../types/types';
import { DateChanged, FetchDropOffHoursProps, FetchPickupHoursProps, Picker, PickerDate, ValidateAndFetchPriceProps } from './types';
import { dateToString } from '../../../utils/utils';
import { fetchPossibleHours } from '../../../api';
import { isSameDay } from 'date-fns';

export const makeTimePicker = (picker: WidgetTimePicker): TimeSlotPicker => {
  if (picker) {
    if (picker.widget === WidgetType.FixedDuration) {
      return {
        pickerType: WidgetType.FixedDuration,
        options: picker.durations.map(option => {
          return {
            value: option.slug,
            label: option.label,
          };
        }),
      };
    } else if (picker.widget === WidgetType.FixedHours) {
      return {
        pickerType: WidgetType.FixedHours,
        options: picker.hours.map(option => {
          return {
            value: option.slug,
            label: option.label,
          };
        }),
      };
    }
  }

  return null;
};

export const showTimePickers = (picker: Picker) => {
  return picker.withTime && !picker.timeSlotPicker;
};

export const fetchPickupHours = ({
  itemId,
  selectedVariationId,
  fromDate,
  toDate,
  suspectedDropOffDate,
  withDateRange,
  changeFromTimeCallback,
  changeToTimeCallback,
  changeBookingDateCallback,
  setPossibleHoursCallback,
  loadingCallback,
  fetchOnly,
}: FetchPickupHoursProps) => {
  loadingCallback(true);
  if (!fetchOnly) {
    changeBookingDateCallback({ fromTime: '', toTime: '' });
  }

  const dropOffDate = toDate ? dateToString(toDate) : suspectedDropOffDate ? dateToString(suspectedDropOffDate) : null;

  fetchPossibleHours(
    itemId,
    selectedVariationId,
    dateToString(fromDate),
    dropOffDate,
    (hours: any) => {
      setPossibleHoursCallback(hours);

      const pickupTimeObjectLength: number = Object.keys(hours).length;
      if (pickupTimeObjectLength === 1) {
        const pickupHour = Object.keys(hours)[0];
        if (!fetchOnly) {
          changeFromTimeCallback(pickupHour);
        }

        if (!withDateRange) {
          const dropOffTimeObjectLength: number = hours[pickupHour].length;
          if (dropOffTimeObjectLength === 1 && !fetchOnly) {
            changeToTimeCallback(hours[pickupHour]);
          }
        }
      }

      loadingCallback(false);
    },
    () => loadingCallback(false),
  );
};

export const fetchDropOffHours = ({
  itemId,
  selectedVariationId,
  fromDate,
  toDate,
  fromTime,
  suspectedDropOffDate,
  changeBookingDateCallback,
  changeToTimeCallback,
  setPossibleHoursCallback,
  loadingCallback,
  fetchOnly,
}: FetchDropOffHoursProps) => {
  loadingCallback(true);
  if (!fetchOnly) {
    changeBookingDateCallback({ toTime: '' });
  }

  const endDate = toDate || suspectedDropOffDate;
  if (!endDate) {
    loadingCallback(false);
    return;
  }

  fetchPossibleHours(
    itemId,
    selectedVariationId,
    dateToString(fromDate),
    dateToString(endDate),
    (hours: any) => {
      setPossibleHoursCallback(hours);
      loadingCallback(false);

      const dropOffTimeObjectLength: number = hours[fromTime].length;
      if (dropOffTimeObjectLength === 1 && !fetchOnly) {
        changeToTimeCallback(hours[fromTime][0]);
      }
    },
    () => {
      loadingCallback(false);
    },
  );
};

export const validateAndFetchPrice = ({
  itemId,
  selectedVariationId,
  fromDate,
  toDate,
  suspectedDropOffDate,
  fromTime,
  toTime,
  withDateRange,
  withTime,
  amountToBook,
  selectedTimeSlot,
  priceOptions,
  loadingCallback,
  setPriceCallback,
  setPriceOptionsCallback,
  fetchPriceCallback,
  runWithLoading,
}: ValidateAndFetchPriceProps) => {
  const dateTime = withDateRange && withTime;
  const dateNoTime = withDateRange && !withTime;
  const noDateTime = !withDateRange && withTime;
  const noDateNoTime = !withDateRange && !withTime;

  let runValidate = false;
  if (selectedTimeSlot) {
    runValidate = true;
  } else {
    if (noDateNoTime || dateNoTime) {
      if (fromDate && (suspectedDropOffDate || toDate)) {
        runValidate = true;
      }
    } else if (noDateTime || dateTime) {
      if (toTime) {
        runValidate = true;
      }
    }
  }

  if (runValidate) {
    if (runWithLoading) {
      loadingCallback(true);
    }

    fetchPriceCallback(
      itemId,
      selectedVariationId,
      {
        from: dateToString(fromDate),
        to: toDate || suspectedDropOffDate ? dateToString(toDate || suspectedDropOffDate) : null,
      },
      { from: fromTime, to: toTime },
      selectedTimeSlot,
      amountToBook,
      (data: any) => {
        setPriceCallback({
          available: data.available,
          gross: data.price,
          net: data.netPrice,
          mileageOptions: data.mileageOptions,
          quantity: {
            min: data.minQuantity,
            max: data.maxQuantity,
            available: data.availableQuantity,
          },
        });

        setPriceOptionsCallback({ ...priceOptions, priceCalculation: data.priceCalculationType });
        loadingCallback(false);
      },
      () => loadingCallback(false),
    );
  } else {
    setPriceCallback({ available: false, gross: 0, net: 0, vat: 0, mileageOptions: [] });
  }
};

export const checkSameDay = (from: Date, to: Date): boolean => {
  if (!from && !to) {
    return true;
  } else {
    return isSameDay(from, to);
  }
};

export const calculatedToDate = (details: BookingDetails, daysAvailability): Date => {
  if (details.toDate) {
    return details.toDate;
  }

  if (!details.fromDate || !daysAvailability || !Object.keys(daysAvailability).length) {
    return null;
  }

  const pickUpString = dateToString(details.fromDate);

  const canUsePickupAsDropOff = daysAvailability[pickUpString] && daysAvailability[pickUpString].state === DayState.Selectable;
  if (canUsePickupAsDropOff) {
    return details.fromDate;
  }

  return null;
};

export const bookingDateChanged = (newDetails: BookingDetails, oldDetails: BookingDetails) => {
  // suspectedToDate is not watched for reason
  return (
    !checkSameDay(newDetails.fromDate, oldDetails.fromDate) ||
    !checkSameDay(newDetails.toDate, oldDetails.toDate) ||
    newDetails.fromTime != oldDetails.fromTime ||
    newDetails.toTime != oldDetails.toTime ||
    newDetails.selectedTimeSlot !== oldDetails.selectedTimeSlot
  );
};

export const changeReason = (newDetails: BookingDetails, oldDetails: BookingDetails): DateChanged => {
  return {
    reset: !newDetails.fromDate && !newDetails.toDate && ((!newDetails.fromTime && !newDetails.toTime) || !newDetails.selectedTimeSlot),
    bookingDate: !checkSameDay(newDetails.fromDate, oldDetails.fromDate),
    bookingTime: (!!newDetails.fromTime || !!oldDetails.fromTime) && newDetails.fromTime !== oldDetails.fromTime,
    toDate: !checkSameDay(newDetails.toDate, oldDetails.toDate),
    toTime: (!!newDetails.toTime || !!oldDetails.toTime) && newDetails.toTime !== oldDetails.toTime,
    timeSlot: newDetails.selectedTimeSlot !== oldDetails.selectedTimeSlot,
  };
};

export const bookingVariationChanged = (newDetails: BookingDetails, oldDetails: BookingDetails, itemId) => {
  return newDetails.selectedVariationIds[itemId] !== oldDetails.selectedVariationIds[itemId];
};

export const bookingMileageChanged = (newDetails: BookingDetails, oldDetails: BookingDetails, itemId) => {
  return newDetails.selectedMileageIds[itemId] !== oldDetails.selectedMileageIds[itemId];
};

export const dateTime = (picker: PickerDate) => {
  return picker.withDateRange && picker.withTime;
};

export const dateNoTime = (picker: PickerDate) => {
  return picker.withDateRange && !picker.withTime;
};

export const noDateTime = (picker: PickerDate) => {
  return !picker.withDateRange && picker.withTime;
};

export const noDateNoTime = (picker: PickerDate) => {
  return !picker.withDateRange && !picker.withTime;
};

export const available = (picker: Picker) => {
  if (dateTime(picker)) {
    return !!picker.dropOffTime;
  } else if (dateNoTime(picker)) {
    return !!picker.dropOffDate || !!picker.suspectedDropOffDate;
  } else if (noDateTime(picker)) {
    return !!picker.dropOffTime || !!picker.timeSlotPicker?.selectedOption;
  } else if (noDateNoTime(picker)) {
    return !!picker.pickUpDate;
  }
};
